import { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../utils/api';

interface IdleWarning {
  instance_id: string;
  instance_name: string;
  seconds_remaining: number;
  urgent?: boolean;
}

interface IdleWarningBannerProps {
  warnings: IdleWarning[];
  onDismiss: (instanceId: string) => void;
}

export default function IdleWarningBanner({ warnings, onDismiss }: IdleWarningBannerProps) {
  const [countdowns, setCountdowns] = useState<Record<string, number>>({});
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initial: Record<string, number> = {};
    for (const w of warnings) {
      // Only set if not already tracking (preserve live countdown)
      if (countdowns[w.instance_id] === undefined) {
        initial[w.instance_id] = w.seconds_remaining;
      }
    }
    if (Object.keys(initial).length > 0) {
      setCountdowns(prev => ({ ...prev, ...initial }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnings]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdowns(prev => {
        const next: Record<string, number> = {};
        for (const [id, secs] of Object.entries(prev)) {
          if (secs > 0) next[id] = secs - 1;
          else next[id] = 0;
        }
        return next;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleKeepAlive = async (instanceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/keep-alive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      onDismiss(instanceId);
    } catch (err) {
      console.error('Keep-alive failed:', err);
    }
  };

  const handleTerminate = async (instanceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/terminate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      onDismiss(instanceId);
    } catch (err) {
      console.error('Terminate failed:', err);
    }
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const activeWarnings = warnings.filter(w => countdowns[w.instance_id] !== undefined);
  if (activeWarnings.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {activeWarnings.map(w => {
        const secs = countdowns[w.instance_id] ?? 0;
        const isUrgent = w.urgent;
        return (
          <div
            key={w.instance_id}
            className={isUrgent
              ? "bg-red-500/10 border border-red-500/40 rounded-lg p-4 animate-pulse"
              : "bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
            }
          >
            <div className="flex items-start">
              <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.345 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-medium ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
                  {isUrgent ? `Terminating in ${formatTime(secs)}` : `Idle shutdown in ${formatTime(secs)}`}
                </h3>
                <p className="text-sm text-gray-300 mt-1">
                  <span className="font-mono text-white">{w.instance_name}</span>
                  {isUrgent
                    ? ' has been idle for 15 minutes. Click "Keep Working" to prevent shutdown.'
                    : ' will be terminated due to inactivity.'}
                </p>
              </div>
              <div className="flex space-x-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => handleKeepAlive(w.instance_id)}
                  className={`px-3 py-1.5 text-white rounded text-xs font-medium transition-colors ${
                    isUrgent ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-600 hover:bg-amber-500'
                  }`}
                >
                  Keep Working
                </button>
                <button
                  onClick={() => handleTerminate(w.instance_id)}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-red-600 text-gray-300 hover:text-white rounded text-xs font-medium transition-colors"
                >
                  Terminate
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
